'use client';

import { useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';

import { contenuDe, type LangueVitrine } from '@/features/marketing/i18n';
import {
  type ContactFieldError,
  type ContactFormValues,
  contactPayload,
  EMPTY_CONTACT_FORM,
  validateContactForm,
} from '@/features/marketing/lib/contactForm';
import { PhoneInput } from '@/shared/components/molecules/PhoneInput';
import { publicHttpClient } from '@/shared/libs/publicHttpClient';
import { cn } from '@/shared/utils/cn';

/** `POST /public/support/contact` — sans slug : c'est le site de l'éditeur, pas celui d'un tenant. */
const CONTACT_URL = '/api/v1/public/support/contact';

type ContactResponse = { reference?: string | null };

/**
 * Le formulaire de contact de la vitrine (#1308).
 *
 * Il crée un ticket dans la corbeille de l'éditeur (#1297) et rend sa **référence** au prospect :
 * sans elle, il n'a rien à citer s'il rappelle.
 *
 * **Rien ne se perd à l'échec.** Une erreur laisse la saisie en place : un message de dix lignes
 * reperdu, personne ne le réécrit — et c'est précisément le prospect le plus intéressé qui écrit
 * long.
 *
 * **Aucune promesse de délai.** La page dit ce qui se passe, pas quand : un « réponse sous 24 h »
 * que personne ne tient coûte plus qu'il ne rassure.
 *
 * **Le champ leurre part au serveur**, qui rend alors la même réponse qu'un envoi réussi
 * (suivora-api#976). Le bloquer ici en plus donnerait deux règles pour une même protection, et
 * celle du navigateur ne protège de rien : un robot poste directement sur la route.
 */
/*
  **Les motifs proposés vivent dans le dictionnaire** (#1342, traduits en #2374).

  Ils couvrent ce qu'on écrit dans neuf demandes sur dix, et « Autre » rouvre la saisie libre :
  aucune liste ne couvre tout, et forcer un choix ferait cocher n'importe quoi.

  ⚠️ Ils étaient écrits en dur ici. Les y laisser aurait donné un formulaire anglais proposant des
  motifs français — le genre de détail qui trahit une traduction faite à moitié.
*/

/**
 * Le même formulaire sert la demande de démonstration et la page de support (#1821) — c'est la
 * même corbeille et la même référence rendue. Ce qui change tient en deux choses : les motifs
 * proposés, et le nombre de portés.
 *
 * **Le nombre de consultants portés ne se demande pas à qui vient signaler une panne.** Il situe un
 * prospect dans la grille tarifaire ; sur une page de support, c'est une question sans usage posée
 * à quelqu'un qui a un problème.
 */
type Props = {
  langue?: LangueVitrine;
  /** Motifs proposés. Par défaut, ceux de la demande de démonstration. */
  subjectPresets?: readonly string[];
  /** Demander le nombre de consultants portés — vrai par défaut, faux hors contexte commercial. */
  askPortedCount?: boolean;
};

export default function ContactForm({
  subjectPresets,
  askPortedCount = true,
  langue = 'fr',
}: Props = {}) {
  const c = contenuDe(langue).formulaire;
  /* Les motifs viennent de la langue lue ; un appelant peut toujours imposer les siens. */
  const motifs = subjectPresets ?? c.motifs;
  const [customSubject, setCustomSubject] = useState(false);
  const [values, setValues] = useState<ContactFormValues>(EMPTY_CONTACT_FORM);
  const [errors, setErrors] = useState<ContactFieldError[]>([]);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [sent, setSent] = useState<{ reference: string | null } | null>(null);

  const set = (field: keyof ContactFormValues) => (value: string) => {
    setValues(current => ({ ...current, [field]: value }));
    setErrors(current => current.filter(e => e !== field));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    const found = validateContactForm(values);
    if (found.length) {
      setErrors(found);
      return;
    }

    setSending(true);
    setFailure(null);
    try {
      const response = await publicHttpClient<ContactResponse>(CONTACT_URL, {
        method: 'POST',
        body: JSON.stringify(contactPayload(values)),
      });
      setSent({ reference: response?.reference ?? null });
    } catch (error) {
      const status = (error as { status?: number }).status;
      setFailure(
        status === 404
          ? c.erreurIndisponible
          : status === 429
            ? c.erreurTropDeDemandes
            : c.erreurEnvoi,
      );
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="flex min-h-[26rem] flex-col items-center justify-center text-center">
        <span className="flex size-14 items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-600">
          <CheckCircle2 className="size-6" />
        </span>
        <p className="mt-6 text-lg font-bold text-slate-900">{c.confirmation}</p>
        {/*
          La référence est rendue quand l'API en donne une : c'est ce qu'on cite au téléphone. Son
          absence ne se comble pas par un numéro inventé — le message est parti, c'est l'essentiel.
        */}
        {sent.reference && (
          <p className="mt-2 text-[14px] text-slate-600">
            Sa référence est{' '}
            <span className="font-mono font-semibold text-slate-900">{sent.reference}</span>.
          </p>
        )}
        <p className="mt-3 max-w-sm text-[14px] leading-relaxed text-slate-600">
          Nous revenons vers vous à l’adresse indiquée. Inutile de renvoyer le formulaire.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={c.nom}
          placeholder={c.nomPlaceholder}
          value={values.senderName}
          onChange={set('senderName')}
          error={errors.includes('senderName')}
          autoComplete="name"
          required
        />
        <Field
          label={c.societe}
          placeholder={c.societePlaceholder}
          value={values.companyName}
          onChange={set('companyName')}
          error={errors.includes('companyName')}
          autoComplete="organization"
          required
        />
        <Field
          label={c.email}
          type="email"
          placeholder={c.emailPlaceholder}
          value={values.senderEmail}
          onChange={set('senderEmail')}
          error={errors.includes('senderEmail')}
          hint={errors.includes('senderEmail') ? c.emailRaison : undefined}
          autoComplete="email"
          required
        />
        <PhoneInput
          label={c.telephone}
          value={values.phone}
          onChange={set('phone')}
          hint={c.facultatif}
        />
      </div>

      {/*
        Un nombre à deux chiffres n'a pas besoin de toute la largeur (#1344) : le champ s'étirait
        sur neuf cents pixels pour recevoir « 45 ».
      */}
      {askPortedCount && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={c.portes}
            type="number"
            placeholder={c.portesPlaceholder}
            value={values.portedCount}
            onChange={set('portedCount')}
            error={errors.includes('portedCount')}
            /* Facultatif, et dit comme tel : un prospect qui ne le sait pas encore doit pouvoir écrire. */
            hint={errors.includes('portedCount') ? c.portesHint : c.portesHint}
          />
        </div>
      )}

      {/*
        L'objet était un champ vide à remplir avant d'écrire son message (#1342) : sur un
        formulaire de démonstration, c'est la question qui fait abandonner — on ne sait pas quoi y
        mettre, et on cherche une formule. Les motifs courants sont proposés ; « Autre » rouvre la
        saisie libre, parce qu'aucune liste ne couvre tout.
      */}
      <div className="flex flex-col gap-2">
        <span className="text-[13px] font-semibold text-slate-800">
          Votre demande <span className="text-primary-600">*</span>
        </span>
        <div className="flex flex-wrap gap-2">
          {motifs.map(preset => {
            const active = values.subject === preset;
            return (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  setCustomSubject(false);
                  set('subject')(active ? '' : preset);
                }}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-[13px] font-medium transition-all',
                  active
                    ? 'border-primary-500 bg-primary-50 text-primary-800'
                    : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400',
                )}
              >
                {preset}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => {
              setCustomSubject(true);
              set('subject')('');
            }}
            className={cn(
              'rounded-full border px-3 py-1.5 text-[13px] font-medium transition-all',
              customSubject
                ? 'border-primary-500 bg-primary-50 text-primary-800'
                : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400',
            )}
          >
            {c.autre}
          </button>
        </div>
        {customSubject && (
          <input
            type="text"
            value={values.subject}
            onChange={e => set('subject')(e.target.value)}
            placeholder={c.objetPlaceholder}
            className={cn(
              'focus:border-primary-500 focus:ring-primary-500/20 mt-1 h-[42px] rounded-xl border bg-white px-3.5 text-[14px] transition-colors focus:ring-2 focus:outline-none',
              errors.includes('subject') ? 'border-red-300' : 'border-slate-300',
            )}
          />
        )}
        {errors.includes('subject') && !customSubject && (
          <p className="text-[12px] text-red-700">{c.motifManquant}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[13px] font-semibold text-slate-800">
          Votre message <span className="text-primary-600">*</span>
        </label>
        <textarea
          rows={5}
          value={values.message}
          onChange={e => set('message')(e.target.value)}
          placeholder={c.messagePlaceholder}
          className={cn(
            'focus:border-primary-500 focus:ring-primary-500/20 rounded-xl border bg-white px-3.5 py-2.5 text-[14px] transition-colors focus:ring-2 focus:outline-none',
            errors.includes('message') ? 'border-red-300' : 'border-slate-300',
          )}
        />
      </div>

      {/*
        Le champ leurre. Invisible pour un humain, hors du parcours clavier, ignoré du
        remplissage automatique — et parfaitement visible pour un robot qui lit le HTML.
      */}
      <div aria-hidden className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0">
        <label htmlFor="website">{c.piege}</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={e => set('website')(e.target.value)}
        />
      </div>

      {failure && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] leading-relaxed text-red-800">
          {failure}
        </p>
      )}

      <button
        type="submit"
        disabled={sending}
        className="from-primary-500 to-primary-700 shadow-primary-600/25 mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-br px-5 py-3 text-[15px] font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:opacity-60"
      >
        {sending ? <Loader2 className="size-4 animate-spin" /> : null}
        {sending ? 'Envoi…' : 'Envoyer ma demande'}
        {!sending && <ArrowRight className="size-4" />}
      </button>

      <p className="text-[12px] leading-relaxed text-slate-500">
        Vos coordonnées servent à répondre à cette demande, et à rien d’autre. Voir la{' '}
        <a href="/confidentialite" className="text-primary-700 hover:underline">
          politique de confidentialité
        </a>
        .
      </p>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  error,
  hint,
  required,
  autoComplete,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  error?: boolean;
  hint?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-semibold text-slate-800">
        {label} {required && <span className="text-primary-600">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className={cn(
          'focus:border-primary-500 focus:ring-primary-500/20 h-[42px] rounded-xl border bg-white px-3.5 text-[14px] transition-colors focus:ring-2 focus:outline-none',
          error ? 'border-red-300' : 'border-slate-300',
        )}
      />
      {hint && (
        <p className={cn('text-[12px]', error ? 'text-red-700' : 'text-slate-500')}>{hint}</p>
      )}
    </div>
  );
}
